import { ChangeDetectorRef, Component, Inject, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, finalize, map, Observable, of, startWith, switchMap } from 'rxjs';
import { Generic } from 'src/app/interfaces/generic.interface';
import { notDefaultValidator } from 'src/app/shared/validators/not-default';
import { helper } from 'src/environments/helper';
import { TipuriDocumenteService } from '../../../settings-sidenav/tipuri-documente/tipuri-documente.service';
import { NotificationService } from 'src/app/services/notification.service';
import { DocumenteCetateanService } from '../documente-cetatean.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonService } from 'src/app/services/common.service';
import { StorageService } from 'src/app/utils/storage/storage.service';
import { AuthService } from 'src/app/services/auth.service';
import { environment } from 'src/environments/environment';
import { DocumenteCetateanEditPayload } from '../documente-cetatean.interface';
import { MatSelect } from '@angular/material/select';

@Component({
    selector: 'app-edit-document-cetatean',
    templateUrl: './edit-document-cetatean.component.html',
    styleUrls: ['./edit-document-cetatean.component.scss']
})
export class EditDocumentCetateanComponent {
    @ViewChild('directieSelect') directieSelect!: MatSelect;

    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;

    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;

    helper = helper;

    token!: string;
    isLoading = false;
    isCnpLoading = false;

    dropdownControl = new FormControl(this.data.cnp_cif);
    cnps: any[] = [];
    showMessage = true;
    loadComplete = false;

    directii!: Generic[];
    filteredDirectii!: Observable<Generic[]>;
    filterControl = new FormControl('');
    tipuriDocumente!: Generic[];
    compartimenteActive$!: Observable<Generic[]>;
    user!: any;

    defaultParameter: { name: string; id: string } = {
        name: '-- Alegeți --',
        id: '-1',
    };

    notificareParameters: { key: string; value: boolean }[] = [
        { key: 'Nu', value: false },
        { key: 'Da', value: true },
    ];

    form: FormGroup = new FormGroup({
        cnp_cif: new FormControl(this.data.cnp_cif, [Validators.required]),
        directie_id: new FormControl(this.data.directie_id, [Validators.required, notDefaultValidator("-1")]),
        directie_name: new FormControl(this.data.directie_name, [Validators.required]),
        compartiment_id: new FormControl(this.data.compartiment_id),
        compartiment_name: new FormControl(this.data.compartiment_name),
        document_type: new FormControl(this.data.document_type, [Validators.required, notDefaultValidator("-1")]),
        document_type_name: new FormControl(this.data.document_type_name, [Validators.required]),
        notificare_sms_allow: new FormControl(this.data.notificare_sms_allow),
        notificare_sms_number: new FormControl(this.data.notificare_sms_number),
        notificare_email_allow: new FormControl(this.data.notificare_email_allow),
        notificare_email_address: new FormControl(this.data.notificare_email_address, [Validators.required]),
    });

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: DocumenteCetateanEditPayload,
        private dialogRef: MatDialogRef<EditDocumentCetateanComponent>,
        private documenteCetateanService: DocumenteCetateanService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private commonService: CommonService,
        private tipuriDocumenteService: TipuriDocumenteService,
        public toastr: ToastrService,
        private cdr: ChangeDetectorRef,
    ) {
        this.processToken();
        this.fetchCnps(this.data?.cnp_cif)
        this.dropdownControl.valueChanges
            .pipe(debounceTime(600))
            .subscribe((cnp: any) => {
                this.fetchCnps(cnp);
                this.showMessage = true;
                this.loadComplete = false;
            });
    }

    ngOnInit(): void {
        Promise.all([this.loadDirectiiActive()])
            .then(() => {
                this.loadTipuriDocumente();
                this.loadCompartimenteActive(this.data.directie_id);
                this.filteredDirectii = this.filterControl.valueChanges.pipe(
                    startWith(''),
                    map((value: any) => this.filterDirectii(value))
                );
            })
            .catch(error => {
                console.error('Error loading data', error);
            });
    }

    ngAfterViewInit() {
        this.directieSelect.openedChange.subscribe((opened) => {
            if (opened) {
                setTimeout(() => {
                    const panel = this.directieSelect.panel.nativeElement;
                    panel.scrollTo({ top: 0, behavior: 'smooth' });
                });
            }
        });
    }

    filterDirectii(value: string): Generic[] {
        const filterValue = value.toLowerCase();
        return this.directii.filter(option => option.name.toLowerCase().includes(filterValue));
    }

    fetchCnps(cnp: string) {
        if (cnp.length >= 2) {
            this.isCnpLoading = true;
            this.documenteCetateanService.getCnp(cnp).subscribe({
                next: (response: any) => {
                    this.cnps = response.data;
                    this.isCnpLoading = false;
                    this.loadComplete = true;
                    this.showMessage = this.cnps.length === 0;
                },
                error: (err: any) => {
                    this.isCnpLoading = false;
                    this.showMessage = true;
                    this.loadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.cnps = [];
            this.showMessage = true;
            this.loadComplete = false;
        }
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.documenteCetateanService.setToken(token);
        });
    }

    loadDirectiiActive(): Promise<void> {
        this.isLoading = true;
        return this.commonService
            .getDirectiiActive()
            .pipe(
                map((results: Generic[]) => [this.defaultParameter, ...results]),
                finalize(() => (this.isLoading = false))
            )
            .toPromise()
            .then((results: any) => {
                this.directii = results;
            })
            .catch((error) => {
                console.error('Error loading directii', error);
                this.isLoading = false;
            });
    }
    onDirectieChange(directieId: number): void {
        const selectedDirectie = this.directii.find(directie => directie.id === directieId);

        if (directieId) {
            this.loadCompartimenteActive(directieId);
            this.form.get('directie_name')?.patchValue(selectedDirectie?.name);
        } else {
            this.compartimenteActive$ = new Observable<Generic[]>((observer) => {
                observer.complete();
            });
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        this.compartimenteActive$ = compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isLoading = false;
            })
        );
    }

    onCompartimenteChange(compartimentId: number) {
        this.compartimenteActive$.pipe(
            switchMap(compartimente => of(compartimente)),
            map(compartimente => compartimente.find(compartiment => compartiment.id === compartimentId))
        ).subscribe(selectedCompartiment => {
            if (selectedCompartiment) {
                this.form.get('compartiment_name')?.patchValue(selectedCompartiment.name);
            }
        });
    }

    loadTipuriDocumente() {
        this.isLoading = true;

        this.tipuriDocumenteService
            .getTipuriDocumenteActive()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results]),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (results) => {
                    this.tipuriDocumente = results;
                },
                error: (error) => {
                    console.error('Error loading tipuri documente', error);
                    this.isLoading = false;
                },
            });
    }

    onTipDocumentSelected(id: number) {
        const selectedTip = this.tipuriDocumente.find(tip => tip.id === id);

        if (selectedTip) {
            this.form.get('document_type_name')?.patchValue(selectedTip.name);
        }
    }

    onCnpChange(selectedCnp: string) {
        const selectedCitizen = this.cnps.find(citizen => citizen.cnp === selectedCnp)
        if (selectedCnp) {
            this.form.get('notificare_sms_number')?.patchValue(selectedCitizen.international_number);
            this.form.get('notificare_email_address')?.patchValue(selectedCitizen.email);
        }
    }

    onSmsChange(val: boolean) {
        let sms = this.form.get('notificare_sms_number') as FormControl;

        if (val === true) {
            sms.setValidators([Validators.required]);
        } else {
            sms.clearValidators();
            sms.setErrors(null);
            sms.markAsPristine();
            sms.markAsUntouched();
        }

        this.form.updateValueAndValidity();
        this.cdr.detectChanges();
    }

    onEmailChange(val: boolean) {
        let email = this.form.get('notificare_email_address') as FormControl;

        if (val === true) {
            email.setValidators([Validators.required]);
        } else {
            email.clearValidators();
            email.setErrors(null);
            email.markAsPristine();
            email.markAsUntouched();
        }

        this.form.updateValueAndValidity();
        this.cdr.detectChanges();
    }

    submitForm() {
        this.isLoading = true;

        const {
            cnp_cif,
            directie_id,
            directie_name,
            compartiment_id,
            compartiment_name,
            document_type,
            document_type_name,
            notificare_sms_allow,
            notificare_sms_number,
            notificare_email_allow,
            notificare_email_address,
        } = this.form.value as DocumenteCetateanEditPayload;

        const cnp_cifNumeric = cnp_cif.replace(/\D/g, '');

        const newDocument: DocumenteCetateanEditPayload = {
            cnp_cif: cnp_cifNumeric,
            directie_id,
            directie_name,
            compartiment_id,
            compartiment_name,
            document_type,
            document_type_name,
            notificare_sms_allow,
            notificare_sms_number,
            notificare_email_allow,
            notificare_email_address,
            id_document: this.data.id_document,
        };

        this.documenteCetateanService
            .updateDocumentCetatean(newDocument)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
